/**
 * Canonical registry of service sub-page routes that actually exist under
 * `app/(site)/<slug>/`. This is the source of truth for two things:
 *
 *  1. The Homepage CMS tab warns admins if they point a service card at a
 *     slug that isn't in this list (so they can't accidentally ship a link
 *     to a 404). See `HomepageTab.tsx`.
 *  2. Each service sub-page filters the gallery to photos whose category
 *     matches its slug. See `components/ServiceGallery.tsx`.
 *
 * When a developer adds a new service page under `app/(site)/<new-slug>/`,
 * they MUST also add the slug here. Keep this list sorted alphabetically
 * for easy diffing.
 */
export const KNOWN_SERVICE_SLUGS = [
  "basement-renovation",
  "bathroom-remodeling",
  "deck-installation",
  "home-additions",
  "home-renovations",
  "kitchen-renovations",
] as const;

export type KnownServiceSlug = (typeof KNOWN_SERVICE_SLUGS)[number];

export function isKnownServiceSlug(slug: string): slug is KnownServiceSlug {
  return (KNOWN_SERVICE_SLUGS as readonly string[]).includes(slug);
}

/**
 * Extract the trailing slug from a site href. Returns "" for an empty or
 * malformed href.
 *
 * Examples:
 *   "/deck-installation/"      → "deck-installation"
 *   "/deck-installation"       → "deck-installation"
 *   "  /Deck-Installation/  "  → "deck-installation"  (trimmed + lowercased)
 *   "https://example.com/foo"  → "foo"                (external → last segment)
 *   ""                         → ""
 */
export function hrefToServiceSlug(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return "";
  // Strip protocol + host if present, keep only the path.
  const pathOnly = trimmed.replace(/^https?:\/\/[^/]+/i, "");
  // Take the final non-empty path segment.
  const parts = pathOnly.split("/").filter(Boolean);
  const last = parts.at(-1) ?? "";
  return last.toLowerCase();
}
